/* eslint-disable @typescript-eslint/no-unused-vars */
import './claim-entity-grid.scss';

import {
  Box,
  Typography,
  Modal,
  Drawer,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Paper,
  Button,
  Autocomplete,
} from '@mui/material';
import { Cancel, FilterList, ExpandMore } from '@mui/icons-material';
import ClaimBordereauModal from './claim-bordereau-modal';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  useDebounce,
  useGetUsers,
  UserGroup,
  useGetEntitiesBrokeragesOptions,
  useGetAllClaimantsQuery,
  useGetAllClaimQuery,
  toPagination,
} from '@converge/graphql';
import { useInfiniteScrollFilter } from '../../../hooks';
import { highlightTextForFilter } from '../utils';
import {
  CheckedState,
  FilterState,
  Option,
} from '../../../presentation/components/table-filter';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { ConvergeButton, NewConvergeButton } from '../../../presentation';
import { ConvergeTooltip } from '../../../presentation/components/converge-tooltip';

export interface ClaimFilterOptions {
  claimNumber: Option[];
  insured: Option[];
  claimant: Option[];
  claimHandler: Option[];
  status: Option[];
  brokerageUuids: Option[];
  loss_date: Option[];
  reportedDate: Option[];
  dueDate: Option[];
}

const defaultFilters = {
  claimNumber: [],
  insured: [],
  claimant: [],
  claimHandler: [],
  status: [],
  brokerageUuids: [],
  loss_date: [],
  reportedDate: [],
  dueDate: [],
};

// Fetch claim handlers from API - we'll use this data instead of the mockClaimHandlers

const allStatusOptions = [
  { label: 'Open', value: 'OPEN' },
  { label: 'Reopened', value: 'REOPENED' },
  { label: 'Closed', value: 'CLOSED' },
];

const claimQueueStatusOptions = [
  { label: 'OPEN', value: 'OPEN' },
  { label: 'REOPENED', value: 'REOPENED' },
];

interface ClaimFilterProps {
  open: boolean;
  onClose?: (filters: CheckedState, filterLabels: CheckedState) => void;
  toggleFilter?: () => void;
  currentFilters: CheckedState;
  setCurrentFilters: (filters: CheckedState) => void;
  currentFilterLabels: CheckedState;
  setCurrentFilterLabels: (filters: CheckedState) => void;
  filterLabels: CheckedState;
  setFilterLabels: (filters: CheckedState) => void;
  setClaimFilters?: (filters: CheckedState) => void;
  resetPageNumber: () => void;
  filterOptions?: ClaimFilterOptions;
  isAllClaims?: boolean;
  isPersonalClaim?: boolean;
}

const ClaimsFilter = ({
  open,
  onClose,
  toggleFilter,
  filterLabels,
  setFilterLabels,
  setCurrentFilterLabels,
  setClaimFilters,
  resetPageNumber,
  currentFilterLabels,
  currentFilters,
  setCurrentFilters,
  filterOptions: providedFilterOptions,
  isAllClaims = true,
  isPersonalClaim = false,
}: ClaimFilterProps) => {
  // Determine which status options to show based on view (memoized to prevent infinite re-renders)
  const statusOptions = useMemo(
    () => (isAllClaims ? allStatusOptions : claimQueueStatusOptions),
    [isAllClaims]
  );

  // Fetch claims users from the API
  const { data: claimUsersList } = useGetUsers({
    userGroups: [UserGroup.Claims],
  });

  const [filterOptions, setFilterOptions] =
    useState<ClaimFilterOptions>(defaultFilters);

  const [allOptions, setAllOptions] =
    useState<ClaimFilterOptions>(defaultFilters);

  // Temporary states for pending filter changes (not applied until "Save Filter" is clicked)
  const [pendingFilters, setPendingFilters] =
    useState<CheckedState>(currentFilters);
  const [pendingFilterLabels, setPendingFilterLabels] =
    useState<CheckedState>(currentFilterLabels);

  const [searchFilters, setSearchFilters] = useState<FilterState>({
    claimNumber: '',
    insured: '',
    claimant: '',
    claimHandler: '',
    status: '',
    brokerageUuids: '',
    loss_date: '',
    reportedDate: '',
    dueDate: '',
  });

  // State for date filters
  const [dateFilters, setDateFilters] = useState<{
    loss_date: [Dayjs | null, Dayjs | null];
    reportedDate: [Dayjs | null, Dayjs | null];
    dueDate: [Dayjs | null, Dayjs | null];
  }>({
    loss_date: [null, null],
    reportedDate: [null, null],
    dueDate: [null, null],
  });

  // State for date filter errors
  const [dateFilterErrors, setDateFilterErrors] = useState<{
    loss_date: string;
    reportedDate: string;
    dueDate: string;
  }>({
    loss_date: '',
    reportedDate: '',
    dueDate: '',
  });

  // State to control modal visibility
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [bordereauOpen, setBordereauOpen] = useState<boolean>(false);

  // State to track which accordion panels are expanded
  const [expandedPanels, setExpandedPanels] = useState<{
    [key: string]: boolean;
  }>({
    claimNumber: false,
    insured: false,
    claimant: false,
    claimHandler: false,
    status: false,
    brokerageUuids: false,
    loss_date: false,
    reportedDate: false,
    dueDate: false,
  });

  const debouncedClaimNumber = useDebounce<string>(
    searchFilters['claimNumber'],
    300
  );

  const debouncedInsured = useDebounce<string>(searchFilters['insured'], 300);

  const debouncedClaimant = useDebounce<string>(searchFilters['claimant'], 300);

  const debouncedClaimHandlerName = useDebounce<string>(
    searchFilters['claimHandler'],
    300
  );

  const debouncedBrokerageUuids = useDebounce<string>(
    searchFilters['brokerageUuids'],
    300
  );

  // State for brokerage, claimant, and insured pagination
  const [brokeragePage, setBrokeragePage] = useState(0);
  const [claimantPage, setClaimantPage] = useState(0);
  const [insuredPage, setInsuredPage] = useState(0);

  // Fetch brokerages with backend search + infinite scroll
  const {
    loading: brokerageListLoading,
    hasNextPage: hasMoreBrokerages,
    fetchMore: fetchMoreBrokerages,
    data: brokerageData,
  } = useGetEntitiesBrokeragesOptions({
    pageNumber: brokeragePage,
    pageSize: 50,
    enabled: modalOpen,
    brokerageName: debouncedBrokerageUuids,
  });

  // Fetch ALL claimants with infinite scroll + backend search
  const {
    loading: claimantListLoading,
    hasNextPage: hasMoreClaimants,
    fetchMore: fetchMoreClaimants,
    data: claimantData,
  } = useGetAllClaimantsQuery({
    pageNumber: claimantPage,
    pageSize: 50,
    enabled: modalOpen,
    nameSearch: debouncedClaimant,
  });

  // Fetch ALL insureds with infinite scroll + backend search
  const {
    loading: insuredListLoading,
    hasNextPage: hasMoreInsureds,
    fetchMore: fetchMoreInsureds,
    insureds,
    data: insuredData,
    pageInfo: insuredPageInfo,
    totalInsureds,
  } = useGetAllClaimQuery({
    pageNumber: insuredPage,
    pageSize: 50,
    enabled: modalOpen,
    insuredName: debouncedInsured,
  });

  // Use reusable hook for brokerage infinite scroll
  const {
    options: allBrokerages,
    loadingMore: loadingMoreBrokerages,
    handleScroll: handleBrokerageScroll,
  } = useInfiniteScrollFilter({
    data: brokerageData,
    loading: brokerageListLoading,
    hasNextPage: hasMoreBrokerages,
    fetchMore: fetchMoreBrokerages,
    transformData: (data) => {
      if (!data?.entities?.edges) return [];
      return data.entities.edges.map((edge) => {
        const node = edge.node;
        const aliases = node.aliases?.edges || [];
        const legalNameAlias = aliases.find(
          (a) => a.node.primary && a.node.type === 'LEGAL_NAME'
        );
        const tradingNameAlias = aliases.find(
          (a) => a.node.primary && a.node.type === 'TRADING_NAME'
        );
        let label = node.referenceId || 'N/A';
        if (legalNameAlias?.node.name?.trim()) {
          label = legalNameAlias.node.name;
        } else if (tradingNameAlias?.node.name) {
          label = tradingNameAlias.node.name;
        }
        return { value: node.uuid || '-', label };
      });
    },
    searchTerm: debouncedBrokerageUuids,
    selectedValues: pendingFilters['brokerageUuids'] || [],
    buildFetchMoreVariables: (nextPage, searchTerm, pageSize) => {
      const aliasFilters: any = { source: 'PRIMARY' };
      if (searchTerm) {
        aliasFilters.name = searchTerm;
      }
      return {
        pagination: { after: pageSize * nextPage, first: pageSize },
        filters: {
          agency: true,
          ...(searchTerm && { brokerageName: searchTerm }),
        },
        aliasFilters,
      };
    },
  });

  // Use reusable hook for claimant infinite scroll
  const {
    options: allClaimantsOptions,
    loadingMore: loadingMoreClaimants,
    handleScroll: handleClaimantScroll,
  } = useInfiniteScrollFilter({
    data: claimantData,
    loading: claimantListLoading,
    hasNextPage: hasMoreClaimants,
    fetchMore: fetchMoreClaimants,
    transformData: (data) => {
      if (!data?.claimants?.edges) return [];
      return data.claimants.edges
        .map((edge) => {
          const node = edge.node;
          if (!node.uuid) return null;
          const label = node.nameSearch;
          return { value: node.uuid, label: label || 'Unknown' };
        })
        .filter((opt): opt is Option => opt !== null);
    },
    searchTerm: debouncedClaimant, // Backend search
    selectedValues: pendingFilters['claimant'] || [],
    buildFetchMoreVariables: (nextPage, searchTerm, pageSize) => ({
      pagination: { after: pageSize * nextPage, first: pageSize },
      claimantFilterInput: searchTerm ? { nameSearch: searchTerm } : {},
      sort: { key: 'NAME_SEARCH', order: 'ASC' },
    }),
  });

  // Use reusable hook for insured infinite scroll
  const {
    options: allInsuredsOptions,
    loadingMore: loadingMoreInsureds,
    handleScroll: handleInsuredScroll,
  } = useInfiniteScrollFilter({
    data: insuredData,
    loading: insuredListLoading,
    hasNextPage: hasMoreInsureds,
    fetchMore: fetchMoreInsureds,
    transformData: (data) => {
      // Transform the raw GraphQL data to options format
      if (!data?.entities?.edges) return [];

      const options: Option[] = [];
      data.entities.edges.forEach((edge: any) => {
        const entity = edge.node;
        if (!entity?.uuid) return;

        const aliases = entity.aliases?.edges || [];
        // Get first alias (already filtered for PRIMARY LEGAL_NAME at query level)
        const label = aliases[0]?.node?.name;

        if (label) {
          options.push({ value: entity.uuid, label });
        }
      });

      return options;
    },
    searchTerm: debouncedInsured, // Backend search
    selectedValues: pendingFilters['insured'] || [],
    buildFetchMoreVariables: (nextPage, searchTerm, pageSize) => ({
      pagination: toPagination(nextPage, pageSize),
      sort: { key: 'PRIMARY_LEGAL_NAME', order: 'ASC' },
      filters: {
        claimInsured: true,
        ...(searchTerm && { alias: searchTerm }),
      },
    }),
  });

  // Reset page when search changes
  useEffect(() => {
    setBrokeragePage(0);
  }, [debouncedBrokerageUuids]);

  useEffect(() => {
    setInsuredPage(0);
  }, [debouncedInsured]);

  useEffect(() => {
    setClaimantPage(0);
  }, [debouncedClaimant]);

  // Initialize filter options with provided data or fallback to mock data
  useEffect(() => {
    // Convert claims users to option format
    const claimHandlerOptions =
      claimUsersList?.users?.edges?.map((edge) => ({
        label: `${edge.node.firstName} ${edge.node.lastName}` || '',
        value: edge.node.uuid || '',
      })) || [];

    // Use accumulated brokerages from all pages
    const brokerageOption = allBrokerages;

    // Use accumulated claimants from all pages
    const claimantOption = allClaimantsOptions;

    // Use accumulated insureds from all pages - backend only, no client-side filtering
    const insuredOption = allInsuredsOptions;

    // Use provided options or fall back to mock/API data
    const baseOptions = {
      claimNumber: providedFilterOptions?.claimNumber?.length
        ? providedFilterOptions.claimNumber
        : [],
      insured: insuredOption || [],
      claimant: claimantOption || [],
      claimHandler: claimHandlerOptions,
      status: statusOptions,
      brokerageUuids: brokerageOption || [],
      loss_date: [],
      reportedDate: [],
      dueDate: [],
    };

    // Set all options
    setAllOptions(baseOptions);

    // Backend handles all filtering - no client-side filtering
    const filteredClaimNumbers = baseOptions.claimNumber;
    const filteredInsureds = baseOptions.insured;
    const filteredHandlers = baseOptions.claimHandler;
    const filteredBrokerages = baseOptions.brokerageUuids;
    const filteredClaimants = baseOptions.claimant;

    const newOptions: ClaimFilterOptions = {
      claimNumber: filteredClaimNumbers,
      insured: filteredInsureds,
      claimant: filteredClaimants,
      claimHandler: filteredHandlers,
      status: statusOptions,
      brokerageUuids: filteredBrokerages,
      loss_date: [],
      reportedDate: [],
      dueDate: [],
    };

    setFilterOptions(newOptions);
  }, [
    providedFilterOptions,
    claimUsersList,
    allBrokerages, // Backend handles all filtering
    allClaimantsOptions, // Backend handles all filtering
    allInsuredsOptions, // Backend handles all filtering
    statusOptions,
  ]);

  const handleCheckboxChange = (
    column: keyof ClaimFilterOptions,
    option: Option
  ) => {
    // Safe check for array
    const currentChecked = Array.isArray(pendingFilters[column])
      ? pendingFilters[column]
      : [];
    const currentLabels = Array.isArray(pendingFilterLabels[column])
      ? pendingFilterLabels[column]
      : [];
    let updatedFilter: string[] = [];
    let updatedFilterLabel: string[] = [];

    // Check if this column supports multiple selections
    const supportsMultiple = [
      'brokerageUuids',
      'claimHandler',
      'insured',
      'status',
      'claimant',
    ].includes(column);

    const valueToStore = option.value;

    if (currentChecked?.includes(valueToStore)) {
      // Remove the item
      updatedFilter = currentChecked.filter((item) => item !== valueToStore);
      updatedFilterLabel = currentLabels.filter(
        (label) => label !== option.label
      );
    } else {
      // Add the item
      if (supportsMultiple) {
        updatedFilter = [...currentChecked, valueToStore];
        updatedFilterLabel = [...currentLabels, option.label];
      } else {
        updatedFilter = [valueToStore];
        updatedFilterLabel = [option.label];
      }
    }

    // Update pending filters (not applied yet)
    const newFilters = {
      ...pendingFilters,
      [column]: updatedFilter,
    };

    const newLabels = {
      ...pendingFilterLabels,
      [column]: updatedFilterLabel,
    };

    // Update pending state only
    setPendingFilters(newFilters);
    setPendingFilterLabels(newLabels);

    // For backend-search columns (brokerage, claimant, insured), don't clear search when selecting - keep results visible
    // For other columns, clear search and show all options
    if (
      column !== 'brokerageUuids' &&
      column !== 'claimant' &&
      column !== 'insured'
    ) {
      // Clear the search filter for this column
      setSearchFilters({ ...searchFilters, [column]: '' });

      // Reset filter options to show all options
      setFilterOptions((prevOptions) => ({
        ...prevOptions,
        [column]: allOptions[column],
      }));
    }
  };

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    column: keyof ClaimFilterOptions
  ) => {
    const inputValue = e.target.value;
    const searchValue = inputValue.toLowerCase();

    setSearchFilters({ ...searchFilters, [column]: inputValue });

    if (searchValue) {
      // Filter options based on the search value
      const filteredOptions = allOptions[column]?.filter((option) =>
        option?.label?.toLowerCase().includes(searchValue)
      );

      setFilterOptions((prevOptions) => ({
        ...prevOptions,
        [column]: filteredOptions,
      }));
    } else {
      // Reset to all options when search is cleared
      setFilterOptions((prevOptions) => ({
        ...prevOptions,
        [column]: allOptions[column],
      }));
    }
  };

  // Handle accordion panel toggle
  const handleAccordionChange = (panel: string) => {
    setExpandedPanels({
      ...expandedPanels,
      [panel]: !expandedPanels[panel],
    });
  };

  // Handle modal open/close
  const handleModalOpen = () => {
    setModalOpen(true);
    // Initialize pending filters with current applied filters when opening modal
    setPendingFilters(currentFilters);
    setPendingFilterLabels(currentFilterLabels);

    // Reset search filters when opening modal
    setSearchFilters({
      claimNumber: '',
      insured: '',
      claimant: '',
      claimHandler: '',
      status: '',
      brokerageUuids: '',
      loss_date: '',
      reportedDate: '',
      dueDate: '',
    });

    setTimeout(() => {
      setFilterOptions({
        claimNumber: allOptions.claimNumber || [],
        insured: allOptions.insured || [],
        claimant: allOptions.claimant || [],
        claimHandler: allOptions.claimHandler || [],
        status: allOptions.status || [],
        brokerageUuids: allOptions.brokerageUuids || [],
        loss_date: [],
        reportedDate: [],
        dueDate: [],
      });
    }, 350);

    // Restore date filters from saved values
    const newDateFilters: {
      loss_date: [Dayjs | null, Dayjs | null];
      reportedDate: [Dayjs | null, Dayjs | null];
      dueDate: [Dayjs | null, Dayjs | null];
    } = {
      loss_date: [null, null],
      reportedDate: [null, null],
      dueDate: [null, null],
    };

    // Parse loss_date
    if (currentFilters['loss_date']?.length > 0) {
      try {
        const dateRange = JSON.parse(currentFilters['loss_date'][0]);
        newDateFilters.loss_date = [
          dayjs(dateRange.start),
          dayjs(dateRange.end),
        ];
      } catch (e) {
        console.error('Error parsing saved loss_date:', e);
      }
    }

    // Parse reportedDate
    if (currentFilters['reportedDate']?.length > 0) {
      try {
        const dateRange = JSON.parse(currentFilters['reportedDate'][0]);
        newDateFilters.reportedDate = [
          dayjs(dateRange.start),
          dayjs(dateRange.end),
        ];
      } catch (e) {
        console.error('Error parsing saved reportedDate:', e);
      }
    }

    // Parse dueDate
    if (currentFilters['dueDate']?.length > 0) {
      try {
        const dateRange = JSON.parse(currentFilters['dueDate'][0]);
        newDateFilters.dueDate = [dayjs(dateRange.start), dayjs(dateRange.end)];
      } catch (e) {
        console.error('Error parsing saved dueDate:', e);
      }
    }

    setDateFilters(newDateFilters);

    // Clear any existing date errors when opening modal
    setDateFilterErrors({
      loss_date: '',
      reportedDate: '',
      dueDate: '',
    });
  };

  const handleModalClose = () => {
    setModalOpen(false);
    // Reset all search filters when closing modal
    setSearchFilters({
      claimNumber: '',
      insured: '',
      claimant: '',
      claimHandler: '',
      status: '',
      brokerageUuids: '',
      loss_date: '',
      reportedDate: '',
      dueDate: '',
    });
    setDateFilters({
      loss_date: [null, null],
      reportedDate: [null, null],
      dueDate: [null, null],
    });
    // Clear date filter errors
    setDateFilterErrors({
      loss_date: '',
      reportedDate: '',
      dueDate: '',
    });
    // Reset pending filters to current applied filters (discard changes)
    setPendingFilters(currentFilters);
    setPendingFilterLabels(currentFilterLabels);
  };

  // Apply pending filters when "Save Filter" is clicked
  const handleSaveFilter = () => {
    // Apply pending filters to actual filters
    setCurrentFilters(pendingFilters);
    setCurrentFilterLabels(pendingFilterLabels);
    setFilterLabels(pendingFilterLabels);

    const filterKey = isAllClaims ? 'allClaimsFilters' : 'claimQueueFilters';
    const labelKey = isAllClaims
      ? 'allClaimsFilterLabels'
      : 'claimQueueFilterLabels';

    setClaimFilters && setClaimFilters(pendingFilters);
    localStorage.setItem(filterKey, JSON.stringify(pendingFilters));
    localStorage.setItem(labelKey, JSON.stringify(pendingFilterLabels));

    // Reset page number when filter changes
    resetPageNumber();

    // Close modal
    handleModalClose();
  };

  // Clear a specific filter
  const handleClearFilter = (
    filterKey: keyof CheckedState,
    e?: React.MouseEvent
  ) => {
    if (e) {
      e.stopPropagation();
    }

    // Update both filter values and labels
    const updatedFilterLabels = {
      ...currentFilterLabels,
      [filterKey]: [],
    };

    const updatedFilters = {
      ...currentFilters,
      [filterKey]: [],
    };

    // Update all state
    setFilterLabels(updatedFilterLabels);
    setCurrentFilterLabels(updatedFilterLabels);
    setCurrentFilters(updatedFilters);

    // Update localStorage with separate keys for All Claims and Claim Queue
    const storageFilterKey = isAllClaims
      ? 'allClaimsFilters'
      : 'claimQueueFilters';
    const storageLabelKey = isAllClaims
      ? 'allClaimsFilterLabels'
      : 'claimQueueFilterLabels';

    setClaimFilters && setClaimFilters(updatedFilters);
    localStorage.setItem(storageFilterKey, JSON.stringify(updatedFilters));
    localStorage.setItem(storageLabelKey, JSON.stringify(updatedFilterLabels));

    resetPageNumber();
  };

  // Clear all pending filters in the modal (not applied yet)
  const handleClearAllFiltersInModal = () => {
    setPendingFilters(defaultFilters);
    setPendingFilterLabels(defaultFilters);
  };

  // Clear all applied filters (from filter chips)
  const handleClearAllFilters = () => {
    // Clear all state
    setCurrentFilters(defaultFilters);
    setCurrentFilterLabels(defaultFilters);
    setFilterLabels(defaultFilters);
    setPendingFilters(defaultFilters);
    setPendingFilterLabels(defaultFilters);

    // Clear localStorage with separate keys for All Claims and Claim Queue
    const filterKey = isAllClaims ? 'allClaimsFilters' : 'claimQueueFilters';
    const labelKey = isAllClaims
      ? 'allClaimsFilterLabels'
      : 'claimQueueFilterLabels';
    const insuredStorageKey = isPersonalClaim
      ? 'claimQueueInsureds'
      : 'allClaimsInsureds';

    setClaimFilters && setClaimFilters(defaultFilters);
    localStorage.setItem(filterKey, JSON.stringify(defaultFilters));
    localStorage.setItem(labelKey, JSON.stringify(defaultFilters));
    localStorage.removeItem(insuredStorageKey);

    resetPageNumber();
  };

  // Render active filter chips
  const renderActiveFilters = () => {
    const activeFilters = (
      Object.keys(currentFilterLabels) as Array<keyof ClaimFilterOptions>
    ).filter((key) => currentFilterLabels[key]?.length > 0);

    if (activeFilters.length === 0) return null;

    // Get friendly filter names
    const getFilterLabel = (key: keyof ClaimFilterOptions) => {
      const labels: Record<keyof ClaimFilterOptions, string> = {
        claimNumber: 'Claim Number',
        brokerageUuids: 'Brokerage',
        claimHandler: 'Claim Handler',
        loss_date: 'Date of Loss',
        insured: 'Insured',
        reportedDate: 'Reported Date',
        status: 'Status',
        claimant: 'Claimant',
        dueDate: 'Due Date',
      };
      return labels[key] || key.replace(/([A-Z])/g, ' $1').trim();
    };

    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 2 }}>
        {activeFilters.map((filterKey) => {
          const labels = currentFilterLabels[filterKey] || [];
          const filterLabel = getFilterLabel(filterKey);

          // Check if this is a date field
          const isDateField =
            filterKey === 'loss_date' ||
            filterKey === 'reportedDate' ||
            filterKey === 'dueDate';

          // Show each selected value as a separate chip
          return labels.map((label: string, index: number) => (
            <Box
              key={`${filterKey}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                mr: '22px',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                }}
              >
                {isDateField ? `${filterLabel}: ${label}` : label}
              </Typography>
              <Cancel
                sx={{ ml: 1, fontSize: '16px', cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();

                  // For date fields, just clear the entire filter
                  if (isDateField) {
                    const updatedFilters = {
                      ...currentFilters,
                      [filterKey]: [],
                    };

                    const updatedLabels = {
                      ...currentFilterLabels,
                      [filterKey]: [],
                    };

                    // Apply immediately
                    setCurrentFilters(updatedFilters);
                    setCurrentFilterLabels(updatedLabels);
                    setFilterLabels(updatedLabels);
                    setPendingFilters(updatedFilters);
                    setPendingFilterLabels(updatedLabels);

                    // Clear date filter state
                    setDateFilters({
                      ...dateFilters,
                      [filterKey]: [null, null],
                    });

                    // Save to localStorage with correct keys
                    const storageFilterKey = isAllClaims
                      ? 'allClaimsFilters'
                      : 'claimQueueFilters';
                    const storageLabelKey = isAllClaims
                      ? 'allClaimsFilterLabels'
                      : 'claimQueueFilterLabels';

                    setClaimFilters && setClaimFilters(updatedFilters);
                    localStorage.setItem(
                      storageFilterKey,
                      JSON.stringify(updatedFilters)
                    );
                    localStorage.setItem(
                      storageLabelKey,
                      JSON.stringify(updatedLabels)
                    );

                    // Reset page number
                    resetPageNumber();
                    return;
                  }

                  const optionToRemove = allOptions[filterKey]?.find(
                    (opt: Option) => {
                      return opt.label === label;
                    }
                  );
                  let valueToRemove: string | undefined;

                  if (optionToRemove) {
                    valueToRemove = optionToRemove.value;
                  } else {
                    const labelIndex = (
                      currentFilterLabels[filterKey] || []
                    ).indexOf(label);
                    if (labelIndex !== -1) {
                      valueToRemove = currentFilters[filterKey]?.[labelIndex];
                    }
                  }

                  if (valueToRemove) {
                    const updatedFilters = {
                      ...currentFilters,
                      [filterKey]: (currentFilters[filterKey] || []).filter(
                        (v) => v !== valueToRemove
                      ),
                    };

                    const updatedLabels = {
                      ...currentFilterLabels,
                      [filterKey]: (
                        currentFilterLabels[filterKey] || []
                      ).filter((l) => l !== label),
                    };

                    // Apply immediately
                    setCurrentFilters(updatedFilters);
                    setCurrentFilterLabels(updatedLabels);
                    setFilterLabels(updatedLabels);
                    setPendingFilters(updatedFilters);
                    setPendingFilterLabels(updatedLabels);

                    // Save to localStorage with correct keys
                    const storageFilterKey = isAllClaims
                      ? 'allClaimsFilters'
                      : 'claimQueueFilters';
                    const storageLabelKey = isAllClaims
                      ? 'allClaimsFilterLabels'
                      : 'claimQueueFilterLabels';

                    setClaimFilters && setClaimFilters(updatedFilters);
                    localStorage.setItem(
                      storageFilterKey,
                      JSON.stringify(updatedFilters)
                    );
                    localStorage.setItem(
                      storageLabelKey,
                      JSON.stringify(updatedLabels)
                    );

                    // Reset page number
                    resetPageNumber();
                  }
                }}
              />
            </Box>
          ));
        })}
      </Box>
    );
  };

  // Handle infinite scroll for brokerage options
  // Render accordion panel for each filter
  const renderFilterAccordion = (
    column: keyof ClaimFilterOptions,
    label: string
  ) => {
    // All columns now store UUID values
    const valueToCheck = 'value';

    // Get all selected options for this column (use pendingFilters for modal display)
    const selectedOptions =
      filterOptions[column]?.filter((option) =>
        pendingFilters[column]?.includes(option[valueToCheck])
      ) || [];

    // Also check in allOptions if not found in filtered options
    const allSelectedOptions =
      allOptions[column]?.filter((option) =>
        pendingFilters[column]?.includes(option[valueToCheck])
      ) || [];

    const finalSelectedOptions =
      selectedOptions.length > 0 ? selectedOptions : allSelectedOptions;

    const selectedCount = pendingFilters[column]?.length || 0;
    const displayLabel =
      selectedCount > 0 ? `${label} (${selectedCount})` : label;

    // Check if this column supports multiple selections
    const supportsMultiple = [
      'brokerageUuids',
      'claimHandler',
      'insured',
      'status',
      'claimant',
    ].includes(column);

    return (
      <Accordion
        expanded={expandedPanels[column]}
        onChange={() => handleAccordionChange(column)}
        sx={{
          boxShadow: 'none',
          '&:before': { display: 'none' },
          borderBottom: '1px solid #e0e0e0',
          margin: '0 !important',
          '&.Mui-expanded': {
            margin: '0 !important',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            minHeight: '56px',
            height: '56px',
            '& .MuiAccordionSummary-content': {
              margin: '0',
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              order: -1,
              marginRight: '12px',
              color: 'black',
              transform: 'rotate(-90deg)',
              transition: 'transform 0.3s',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(0deg)',
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '.5px',
            }}
          >
            {displayLabel}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ marginLeft: '38px', mb: 2, width: '85%' }}>
            <Autocomplete
              size="small"
              fullWidth
              multiple={supportsMultiple}
              disableClearable={
                column === 'brokerageUuids' || column === 'claimant'
              }
              freeSolo={true}
              filterOptions={(x) => x}
              options={[]}
              open={false}
              getOptionLabel={(option): string => {
                if (typeof option === 'string') return option;
                if (option && typeof option === 'object' && 'label' in option) {
                  return String(option.label || '');
                }
                return '';
              }}
              value={supportsMultiple ? ([] as Option[]) : null}
              isOptionEqualToValue={(
                option: Option | string,
                value: Option | string
              ) => {
                if (!option || !value) return false;
                if (typeof option === 'string' || typeof value === 'string')
                  return false;
                if (Array.isArray(option) || Array.isArray(value)) return false;
                return (option as Option).value === (value as Option).value;
              }}
              onChange={(event, newValue) => {
                // This won't be called since dropdown is disabled
              }}
              inputValue={searchFilters[column]}
              onInputChange={(event, newInputValue, reason) => {
                // Update search filter for all reasons except 'reset'
                if (reason === 'reset') {
                  return;
                }

                if (reason === 'clear') {
                  // Don't allow automatic clearing for backend-search columns - user must delete manually
                  if (
                    column === 'brokerageUuids' ||
                    column === 'claimant' ||
                    column === 'insured'
                  ) {
                    return;
                  }
                  setSearchFilters({ ...searchFilters, [column]: '' });
                  setFilterOptions((prevOptions) => ({
                    ...prevOptions,
                    [column]: allOptions[column],
                  }));
                  return;
                }

                // Update search input value
                setSearchFilters({ ...searchFilters, [column]: newInputValue });

                // For brokerageUuids, claimant, and insured, backend handles search via debounce + API call
                // For other columns, do client-side filtering
                if (
                  column !== 'brokerageUuids' &&
                  column !== 'claimant' &&
                  column !== 'insured'
                ) {
                  if (newInputValue && newInputValue.trim() !== '') {
                    const filteredOptions = allOptions[column]?.filter(
                      (option) =>
                        option?.label
                          ?.toLowerCase()
                          .includes(newInputValue.toLowerCase())
                    );
                    setFilterOptions((prevOptions) => ({
                      ...prevOptions,
                      [column]: filteredOptions,
                    }));
                  } else {
                    setFilterOptions((prevOptions) => ({
                      ...prevOptions,
                      [column]: allOptions[column],
                    }));
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`Search ${label}`}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      fontWeight: '600',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'black',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#28c0a7',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#28c0a7',
                        borderWidth: '2px',
                      },
                    },
                  }}
                />
              )}
              ListboxProps={{
                style: {
                  maxHeight: '200px',
                },
              }}
              sx={{
                '& .MuiAutocomplete-option': {
                  fontSize: '14px',
                  fontWeight: '600',
                },
                '& .MuiAutocomplete-popupIndicator': {
                  display: 'none',
                },
                '& .MuiAutocomplete-clearIndicator': {
                  // Hide clear button for brokerage and claimant, show for others
                  display:
                    column === 'brokerageUuids' || column === 'claimant'
                      ? 'none'
                      : 'block',
                  visibility: 'visible',
                  color: 'black',
                },
              }}
            />
          </Box>
          <Box
            sx={{
              marginLeft: '30px',
              width: '85%',
              maxHeight: '250px',
              overflow: 'auto',
            }}
            onScroll={
              column === 'brokerageUuids'
                ? handleBrokerageScroll
                : column === 'claimant'
                ? handleClaimantScroll
                : column === 'insured'
                ? handleInsuredScroll
                : undefined
            }
          >
            <FormGroup>
              {filterOptions[column]?.length > 0 ? (
                filterOptions[column]?.map((option, index) => {
                  // All columns check against value (UUID)
                  const checkValue = option.value;
                  const isChecked =
                    pendingFilters[column]?.includes(checkValue);

                  return (
                    <FormControlLabel
                      key={`${option.value}-${index}`}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleCheckboxChange(column, option)}
                          size="small"
                          sx={{
                            alignSelf: 'flex-start',
                            paddingTop: '2px',
                            color: '#666666',
                            '&.Mui-checked': {
                              color: '#28C0A7',
                            },
                          }}
                        />
                      }
                      label={highlightTextForFilter(
                        option.label,
                        searchFilters[column]
                      )}
                      sx={{
                        alignItems: 'flex-start',
                        margin: '2px 0',
                        '& .MuiFormControlLabel-label': {
                          fontWeight: '600',
                          fontSize: '14px',
                          paddingTop: '3px',
                        },
                      }}
                    />
                  );
                })
              ) : (
                <Typography variant="body2" sx={{ color: '#474343' }}>
                  No results
                </Typography>
              )}
              {column === 'brokerageUuids' && loadingMoreBrokerages && (
                <Typography
                  variant="body2"
                  sx={{ color: '#666', textAlign: 'center', mt: 1, mb: 1 }}
                >
                  Loading more...
                </Typography>
              )}
              {column === 'claimant' && loadingMoreClaimants && (
                <Typography
                  variant="body2"
                  sx={{ color: '#666', textAlign: 'center', mt: 1, mb: 1 }}
                >
                  Loading more...
                </Typography>
              )}
              {column === 'insured' && loadingMoreInsureds && (
                <Typography
                  variant="body2"
                  sx={{ color: '#666', textAlign: 'center', mt: 1, mb: 1 }}
                >
                  Loading more...
                </Typography>
              )}
            </FormGroup>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  // Render date range picker accordion (for Date of Loss and Reported Date)
  const renderDateRangePickerAccordion = (
    column: 'loss_date' | 'reportedDate' | 'dueDate',
    label: string
  ) => {
    const displayLabel = label;

    return (
      <Accordion
        expanded={expandedPanels[column]}
        onChange={() => handleAccordionChange(column)}
        sx={{
          boxShadow: 'none',
          '&:before': { display: 'none' },
          borderBottom: '1px solid #e0e0e0',
          margin: '0 !important',
          '&.Mui-expanded': {
            margin: '0 !important',
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            minHeight: '56px !important',
            height: '56px !important',
            padding: '0 16px !important',
            display: 'flex',
            alignItems: 'center',
            '& .MuiAccordionSummary-content': {
              margin: '0 !important',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiAccordionSummary-content.Mui-expanded': {
              margin: '0 !important',
              minHeight: 'auto !important',
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              order: -1,
              marginRight: '12px',
              color: 'black',
              transform: 'rotate(-90deg)',
              transition: 'transform 0.3s',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(0deg)',
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              textTransform: 'uppercase',
              lineHeight: 1,
            }}
          >
            {displayLabel}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                marginLeft: '38px',
                width: '87%',
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    mb: 1,
                    color: 'black',
                    textTransform: 'uppercase',
                  }}
                >
                  From
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid black',
                    borderRadius: '4px',
                    '&:hover': {
                      borderColor: '#28c0a7',
                    },
                    '&:focus-within': {
                      borderColor: '#28c0a7',
                      borderWidth: '2px',
                    },
                  }}
                >
                  <DatePicker
                    value={dateFilters[column][0]}
                    components={{ OpenPickerIcon: DateRangeIcon }}
                    onChange={(newValue) => {
                      const updatedRange: [Dayjs | null, Dayjs | null] = [
                        newValue,
                        dateFilters[column][1],
                      ];
                      setDateFilters({
                        ...dateFilters,
                        [column]: updatedRange,
                      });
                      const toDate = dateFilters[column][1];

                      // Check if only one date is selected
                      if ((newValue && !toDate) || (!newValue && toDate)) {
                        setDateFilterErrors({
                          ...dateFilterErrors,
                          [column]: 'Both date fields are required',
                        });
                        // Clear the filter when incomplete
                        const updatedFilters = {
                          ...pendingFilters,
                          [column]: [],
                        };
                        const updatedLabels = {
                          ...pendingFilterLabels,
                          [column]: [],
                        };
                        setPendingFilters(updatedFilters);
                        setPendingFilterLabels(updatedLabels);
                      } else if (newValue && toDate) {
                        // Check if end date is earlier than start date
                        if (toDate.isBefore(newValue, 'day')) {
                          setDateFilterErrors({
                            ...dateFilterErrors,
                            [column]:
                              'End date must be greater than or equal to start date',
                          });
                          // Clear the filter when invalid
                          const updatedFilters = {
                            ...pendingFilters,
                            [column]: [],
                          };
                          const updatedLabels = {
                            ...pendingFilterLabels,
                            [column]: [],
                          };
                          setPendingFilters(updatedFilters);
                          setPendingFilterLabels(updatedLabels);
                        } else {
                          setDateFilterErrors({
                            ...dateFilterErrors,
                            [column]: '',
                          });
                          // Format for display
                          const rangeLabel = `${newValue.format(
                            'MM/DD/YYYY'
                          )} - ${toDate.format('MM/DD/YYYY')}`;

                          // Store dates in ISO format for API
                          const rangeValue = JSON.stringify({
                            start: newValue.format('YYYY-MM-DD'),
                            end: toDate.format('YYYY-MM-DD'),
                          });

                          const updatedFilters = {
                            ...pendingFilters,
                            [column]: [rangeValue],
                          };
                          const updatedLabels = {
                            ...pendingFilterLabels,
                            [column]: [rangeLabel],
                          };
                          setPendingFilters(updatedFilters);
                          setPendingFilterLabels(updatedLabels);
                        }
                      } else {
                        // Both dates are null, clear the filter and error
                        setDateFilterErrors({
                          ...dateFilterErrors,
                          [column]: '',
                        });
                        const updatedFilters = {
                          ...pendingFilters,
                          [column]: [],
                        };
                        const updatedLabels = {
                          ...pendingFilterLabels,
                          [column]: [],
                        };
                        setPendingFilters(updatedFilters);
                        setPendingFilterLabels(updatedLabels);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: '',
                        }}
                        size="small"
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '14px',
                            fontWeight: '600',
                            '& fieldset': {
                              border: 'none',
                            },
                            '& input': {
                              textTransform: 'uppercase',
                            },
                            '& input::placeholder': {
                              opacity: 0,
                            },
                            '& .MuiIconButton-root': { color: 'black' },
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    mb: 1,
                    color: 'black',
                    textTransform: 'uppercase',
                  }}
                >
                  To
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid black',
                    borderRadius: '4px',
                    '&:hover': {
                      borderColor: '#28c0a7',
                    },
                    '&:focus-within': {
                      borderColor: '#28c0a7',
                      borderWidth: '2px',
                    },
                  }}
                >
                  <DatePicker
                    value={dateFilters[column][1]}
                    minDate={dateFilters[column][0] || undefined}
                    components={{ OpenPickerIcon: DateRangeIcon }}
                    onChange={(newValue) => {
                      const updatedRange: [Dayjs | null, Dayjs | null] = [
                        dateFilters[column][0],
                        newValue,
                      ];
                      setDateFilters({
                        ...dateFilters,
                        [column]: updatedRange,
                      });
                      const fromDate = dateFilters[column][0];

                      // Check if only one date is selected
                      if ((fromDate && !newValue) || (!fromDate && newValue)) {
                        setDateFilterErrors({
                          ...dateFilterErrors,
                          [column]: 'Both date fields are required',
                        });
                        // Clear the filter when incomplete
                        const updatedFilters = {
                          ...pendingFilters,
                          [column]: [],
                        };
                        const updatedLabels = {
                          ...pendingFilterLabels,
                          [column]: [],
                        };
                        setPendingFilters(updatedFilters);
                        setPendingFilterLabels(updatedLabels);
                      } else if (fromDate && newValue) {
                        // Check if end date is earlier than start date
                        if (newValue.isBefore(fromDate, 'day')) {
                          setDateFilterErrors({
                            ...dateFilterErrors,
                            [column]:
                              'End date must be greater than or equal to start date',
                          });
                          // Clear the filter when invalid
                          const updatedFilters = {
                            ...pendingFilters,
                            [column]: [],
                          };
                          const updatedLabels = {
                            ...pendingFilterLabels,
                            [column]: [],
                          };
                          setPendingFilters(updatedFilters);
                          setPendingFilterLabels(updatedLabels);
                        } else {
                          // Both dates selected and valid, clear error
                          setDateFilterErrors({
                            ...dateFilterErrors,
                            [column]: '',
                          });
                          // Format for display
                          const rangeLabel = `${fromDate.format(
                            'MM/DD/YYYY'
                          )} - ${newValue.format('MM/DD/YYYY')}`;

                          // Store dates in ISO format for API
                          const rangeValue = JSON.stringify({
                            start: fromDate.format('YYYY-MM-DD'),
                            end: newValue.format('YYYY-MM-DD'),
                          });

                          const updatedFilters = {
                            ...pendingFilters,
                            [column]: [rangeValue],
                          };
                          const updatedLabels = {
                            ...pendingFilterLabels,
                            [column]: [rangeLabel],
                          };
                          setPendingFilters(updatedFilters);
                          setPendingFilterLabels(updatedLabels);
                        }
                      } else {
                        // Both dates are null, clear the filter and error
                        setDateFilterErrors({
                          ...dateFilterErrors,
                          [column]: '',
                        });
                        const updatedFilters = {
                          ...pendingFilters,
                          [column]: [],
                        };
                        const updatedLabels = {
                          ...pendingFilterLabels,
                          [column]: [],
                        };
                        setPendingFilters(updatedFilters);
                        setPendingFilterLabels(updatedLabels);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          placeholder: '',
                        }}
                        size="small"
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '14px',
                            fontWeight: '600',
                            '& fieldset': {
                              border: 'none',
                            },
                            '& input': {
                              textTransform: 'uppercase',
                            },
                            '& input::placeholder': {
                              opacity: 0,
                            },
                            '& .MuiIconButton-root': { color: 'black' },
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>
            {dateFilterErrors[column] && (
              <Typography
                sx={{
                  marginLeft: '38px',
                  marginTop: '8px',
                  color: '#D32F2F',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                {dateFilterErrors[column]}
              </Typography>
            )}
          </LocalizationProvider>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <>
      <Box
        className="filter-buttons"
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '28px',
          flexWrap: 'wrap',
          color: 'black',
        }}
      >
        <ConvergeTooltip
          title="Create custom bordereau report"
          placement="bottom"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              border: '1px solid black',
              borderRadius: '3px',
              m: '3px',
              marginRight: '15px',
            }}
            onClick={() => setBordereauOpen(true)}
          >
            <LibraryBooksOutlinedIcon sx={{ padding: '5px 5px' }} />
          </Box>
        </ConvergeTooltip>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            border: '1px solid black',
            borderRadius: '4px',
            marginRight: '10px',
          }}
          onClick={handleModalOpen}
        >
          <FilterList sx={{ padding: '5px 5px' }} />
        </Box>

        {renderActiveFilters()}
        {Object.keys(currentFilterLabels).some(
          (key) => currentFilterLabels[key]?.length > 0
        ) && (
          <Button
            size="small"
            onClick={handleClearAllFilters}
            disableRipple
            sx={{
              ml: 0,
              textAlign: 'left',
              color: 'black',
              textDecoration: 'underline',
              fontSize: '14px',
              fontWeight: 600,
              backgroundColor: 'transparent',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
              '&:focus': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
              '&:active': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
              '&.Mui-focusVisible': {
                backgroundColor: 'transparent',
              },
            }}
          >
            Clear all filters
          </Button>
        )}
      </Box>

      <Drawer
        anchor="left"
        open={modalOpen}
        onClose={handleModalClose}
        aria-labelledby="filter-modal-title"
      >
        <Box
          sx={{
            height: '100vh',
            width: 400,
            bgcolor: 'background.paper',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ overflow: 'auto', flex: 1 }}>
            {isPersonalClaim ? (
              <>
                {renderFilterAccordion('claimHandler', 'Claim Handler')}
                {renderFilterAccordion('claimant', 'Claimant')}
                {renderDateRangePickerAccordion('loss_date', 'Date of Loss')}
                {renderDateRangePickerAccordion('dueDate', 'Due Date')}
                {renderFilterAccordion('insured', 'Insured')}
                {renderDateRangePickerAccordion(
                  'reportedDate',
                  'Reported Date'
                )}
              </>
            ) : (
              <>
                {renderFilterAccordion('brokerageUuids', 'Brokerage')}
                {renderFilterAccordion('claimHandler', 'Claim Handler')}
                {renderFilterAccordion('claimant', 'Claimant')}
                {renderDateRangePickerAccordion('loss_date', 'Date of Loss')}
                {renderFilterAccordion('insured', 'Insured')}
                {renderDateRangePickerAccordion(
                  'reportedDate',
                  'Reported Date'
                )}
                {renderFilterAccordion('status', 'Status')}
              </>
            )}
          </Box>

          <Box
            sx={{
              p: 1,
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              gap: 3,
              height: '39px',
            }}
          >
            <Box style={{ marginLeft: '50px' }}>
              <NewConvergeButton
                variant="outlined"
                label={' Cancel'}
                onClick={handleModalClose}
              />
            </Box>
            <Box sx={{ width: '137px' }}>
              <NewConvergeButton
                variant="contained"
                onClick={handleSaveFilter}
                label=" Save Filters"
                disabled={
                  dateFilterErrors.loss_date !== '' ||
                  dateFilterErrors.reportedDate !== '' ||
                  dateFilterErrors.dueDate !== ''
                }
              />
            </Box>
          </Box>
        </Box>
      </Drawer>

      <ClaimBordereauModal
        open={bordereauOpen}
        onClose={() => setBordereauOpen(false)}
      />
    </>
  );
};

export default ClaimsFilter;
